import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonList,
  IonListHeader,
  IonLabel,
  IonItem,
  IonRadioGroup,
  IonRadio,
  IonIcon,
} from '@ionic/react';
import { closeCircleOutline } from 'ionicons/icons';
import ProPageModal from './ProPageModal';

interface FilterOption {
  value: string;
  label: string;
}

interface FilterDef {
  key: string;
  label: string;
  options: FilterOption[];
}

export interface ProFilterSheetProps {
  /** Controls visibility. */
  isOpen: boolean;
  /** Called when the sheet is dismissed via any path. */
  onDismiss: () => void;
  /** Available filters. */
  filters: FilterDef[];
  /** Currently selected value per filter key. Missing keys default to ''. */
  activeFilters: Record<string, string>;
  /** Called when any filter value changes — applied immediately, no Apply button. */
  onFilterChange: (key: string, value: string) => void;
  /** Optional "clear all" callback. If omitted, Clear All sets every filter to ''. */
  onClearAll?: () => void;
}

/**
 * Pro mobile filter picker presented as a full-page modal.
 *
 * Replaces the per-chip IonActionSheet UX (the old ProFilterChips pattern)
 * with a single modal that shows every filter section at once. Filters
 * apply immediately on selection — there's no Apply button, because the
 * parent page re-queries whenever `activeFilters` changes.
 *
 * Each filter is rendered as an `IonRadioGroup` of `IonItem` rows so the
 * selection is native, accessible, and scroll-friendly.
 */
const ProFilterSheet: React.FC<ProFilterSheetProps> = ({
  isOpen,
  onDismiss,
  filters,
  activeFilters,
  onFilterChange,
  onClearAll,
}) => {
  const { t } = useTranslation('pro');

  const hasActiveFilters = Object.values(activeFilters).some((v) => v !== '' && v != null);

  const handleClearAll = () => {
    if (onClearAll) {
      onClearAll();
      return;
    }
    filters.forEach((f) => onFilterChange(f.key, ''));
  };

  return (
    <ProPageModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      title={t('bottomSheet.filtersTitle')}
      trailingAction={{
        label: t('bottomSheet.done'),
        onClick: onDismiss,
      }}
    >
      {hasActiveFilters && (
        <div className="px-4 pt-3 pb-1">
          <button
            type="button"
            onClick={handleClearAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-transparent text-primary-600 dark:text-primary-300 text-xs font-semibold transition-colors active:bg-primary-50 dark:active:bg-primary-900/20"
          >
            <IonIcon icon={closeCircleOutline} className="text-sm" />
            {t('common.clearFilters')}
          </button>
        </div>
      )}

      {filters.map((filter) => (
        <IonList key={filter.key} inset>
          <IonListHeader>
            <IonLabel className="text-xs font-semibold uppercase tracking-wide">
              {filter.label}
            </IonLabel>
          </IonListHeader>
          <IonRadioGroup
            value={activeFilters[filter.key] ?? ''}
            onIonChange={(e) => onFilterChange(filter.key, e.detail.value)}
          >
            {filter.options.map((opt) => (
              <IonItem key={`${filter.key}-${opt.value || 'all'}`}>
                <IonRadio value={opt.value} justify="start" labelPlacement="end">
                  {opt.label}
                </IonRadio>
              </IonItem>
            ))}
          </IonRadioGroup>
        </IonList>
      ))}
    </ProPageModal>
  );
};

export default ProFilterSheet;
